/**
 * Oráculo Logs Service - Frontend (Vue)
 *
 * ⚠️ MUDANÇA DE ARQUITETURA
 * Antes este serviço assinava os logs com HMAC e postava direto no Oráculo.
 * Isso exigia o segredo no navegador — e todo `VUE_APP_*` é embutido no
 * bundle público pelo webpack, então o segredo ficava exposto a qualquer
 * visitante.
 *
 * Agora os logs vão para o backend V2 (`POST /api/oraculo/logs`), que assina
 * e encaminha. O segredo fica só no servidor, o frontend não precisa conhecer
 * o schema do Oráculo, e o domínio do jogo não precisa estar no CORS do Oráculo.
 *
 * Até 13/09/2026 essa rota morava no Strapi, e este serviço era a última coisa
 * do cliente que ainda falava com ele (`VUE_APP_API_URL`, porta 3000/1337). Hoje
 * o caminho é relativo, como o resto do V2: mesma origem, pelo proxy no dev.
 *
 * Envio em lote: um loop de erros mandando uma requisição por log consumiria
 * a cota de rate limit do jogador e o tiraria do jogo.
 */

import { obterAccessToken } from './api/v2/session';

const API_URL = process.env.VUE_APP_API_V2_URL || '';
const PROXY_PATH = '/api/oraculo/logs';

/** Envia quando acumular esta quantidade. */
const FLUSH_AT_ITEMS = 10;
/** Ou depois deste tempo, o que vier primeiro. */
const FLUSH_INTERVAL_MS = 5000;
/** Teto da fila: além disso, descarta os mais antigos. */
const MAX_QUEUE = 100;
/** Teto por requisição, alinhado com o `OraculoController` do V2. */
const MAX_PER_REQUEST = 50;

class OraculoLogsService {
  constructor() {
    this.endpoint = `${API_URL}${PROXY_PATH}`;
    // Desligado por padrao; liga-se com VUE_APP_TELEMETRY_ENABLED=true. Sem as
    // credenciais do Oraculo no backend a rota responde 202 e descarta.
    this.enabled = process.env.VUE_APP_TELEMETRY_ENABLED === 'true';
    this.debugEnabled = process.env.VUE_APP_DEBUG_MODE === 'true';

    this.queue = [];
    this.timer = null;
    this.sending = false;

    if (typeof window !== 'undefined') {
      // Não perder o que estiver na fila quando a aba fecha ou vai para trás.
      window.addEventListener('beforeunload', () => this.flush(true));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
    }
  }

  /**
   * Normaliza um Error para o formato que o Oráculo aceita.
   *
   * O servidor normaliza de novo — este passo serve para não mandar objetos
   * gigantes (como uma resposta inteira do axios) pela rede.
   *
   * @param {Error|Object|string|null} error
   * @returns {Object|null}
   */
  captureErrorDetails(error) {
    if (!error) return null;
    if (typeof error === 'string') return { message: error };

    const stack = typeof error.stack === 'string' ? error.stack : undefined;

    return {
      type: error.name || error.type || 'Error',
      message: error.message || 'Unknown error',
      stack: stack ? stack.slice(0, 8192) : undefined,
      // `code` precisa ser string no schema; status HTTP vira props no servidor.
      code: error.code !== undefined && error.code !== null ? String(error.code) : undefined,
      status: error.response?.status ?? error.status,
    };
  }

  /**
   * Enfileira um log. Nunca lança e nunca bloqueia a UI.
   * @param {Object} entry
   * @param {string} entry.level
   * @param {string} entry.message
   */
  sendLog(entry) {
    if (!this.enabled || !entry) return;

    try {
      const { level = 'info', message = '', ...rest } = entry;

      // `details` é um agrupador do frontend; achatar evita props aninhado
      // desnecessário e mantém os campos filtráveis no Oráculo.
      const { details, ...metadata } = rest;
      const payload = { level, message, ...metadata, ...(details || {}) };

      this.queue.push(payload);

      if (this.queue.length > MAX_QUEUE) {
        this.queue.splice(0, this.queue.length - MAX_QUEUE);
      }

      if (this.debugEnabled) {
        console.log(`[Oráculo] fila +1 (${this.queue.length}) ${level}: ${message}`);
      }

      if (this.queue.length >= FLUSH_AT_ITEMS) {
        this.flush();
      } else {
        this.scheduleFlush();
      }
    } catch (err) {
      if (this.debugEnabled) console.warn('[Oráculo] falha ao enfileirar log:', err);
    }
  }

  /** Agenda um envio caso ainda não haja um pendente. */
  scheduleFlush() {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  /**
   * Envia a fila para o proxy.
   * @param {boolean} [useBeacon] usa sendBeacon — necessário ao fechar a aba,
   *        onde uma requisição normal seria cancelada.
   */
  flush(useBeacon = false) {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.enabled || this.queue.length === 0) return;
    if (this.sending && !useBeacon) return;

    const logs = this.queue.splice(0, MAX_PER_REQUEST);
    const body = JSON.stringify({ logs });

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(this.endpoint, new Blob([body], { type: 'application/json' }));
      } catch {
        // Aba fechando: não há o que fazer se falhar.
      }
      return;
    }

    this.sending = true;

    // fetch cru em vez do axios do app: o interceptor de erro do axios
    // dispara logs, e logar o erro de logar entra em recursão.
    const headers = { 'Content-Type': 'application/json' };
    // O token so identifica quem loga (`user_id`); a rota e publica e aceita sem ele.
    const token = obterAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    fetch(this.endpoint, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    })
      .catch((err) => {
        if (this.debugEnabled) console.warn('[Oráculo] envio falhou:', err.message);
      })
      .finally(() => {
        this.sending = false;
        if (this.queue.length > 0) this.scheduleFlush();
      });
  }

  // ── Métodos de conveniência por nível ────────────────────────────────────

  debug(message, data = {}) {
    this.sendLog({ level: 'debug', message, ...data });
  }

  info(message, data = {}) {
    this.sendLog({ level: 'info', message, ...data });
  }

  warn(message, data = {}) {
    this.sendLog({ level: 'warn', message, ...data });
  }

  error(message, data = {}) {
    this.sendLog({ level: 'error', message, ...data });
  }

  fatal(message, data = {}) {
    this.sendLog({ level: 'fatal', message, ...data });
  }

  /**
   * Log de autenticação (login/registro).
   */
  logAuth({ action, userId, success, error, details = {} }) {
    this.sendLog({
      level: success ? 'info' : 'error',
      message: success ? `Auth: ${action} successful` : `Auth: ${action} failed`,
      userId,
      event: `auth.${action}`,
      category: 'authentication',
      action,
      success,
      error: error ? this.captureErrorDetails(error) : null,
      details,
    });
  }

  /**
   * Log de erro HTTP capturado no interceptor.
   */
  logHttpError({ method, path, status, error, userId, details = {} }) {
    const errorMessage = details.errorMessage || error?.message || 'HTTP Error';

    this.sendLog({
      level: 'error',
      message: `${errorMessage} (${method} ${path})`,
      userId,
      event: 'http.error',
      category: 'http',
      http: {
        method,
        path,
        // status 0 (erro de rede) é tratado no servidor: sai do contexto HTTP
        // e vira props, porque o schema do Oráculo exige status >= 100.
        status,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      },
      error: error ? this.captureErrorDetails(error) : null,
      details: { ...details, detectedBy: 'axios-interceptor' },
    });
  }
}

export default new OraculoLogsService();
